import { useMemo } from 'react';

/**
 * Inline-SVG sparkline. 14 datapoints look fine at 120×32 with a 1.5px
 * stroke. No external dep — keeps the bundle lean. Uses CSS-var colors so
 * it re-themes when the tenant's theme changes.
 */
export function Sparkline({
  values,
  width = 120,
  height = 32,
  className,
  stroke = 'hsl(var(--foreground))',
}: {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Line color. Defaults to foreground; pass a semantic token for money. */
  stroke?: string;
}) {
  const path = useMemo(() => {
    if (values.length === 0) return '';
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = values.length > 1 ? width / (values.length - 1) : 0;
    return values
      .map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * height;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  }, [values, width, height]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
